import {CreateGoodsReceiptRequest, GoodsReceiptCondition} from './_models'

export type ReceiptItemDraft = {
  requisitionLineId?: number | null
  inventoryItemId?: number | null
  description: string
  partNumber: string
  expectedQty: number
  previouslyReceivedQty: number
  remainingQty: number
  currentReceivedQty: number
  condition: GoodsReceiptCondition
  notes: string
}

const normalizeNumber = (value: unknown): number => {
  if (typeof value === 'number') return value
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return 0
  return Number(trimmed)
}

export const validateGoodsReceiptLines = (
  items: ReceiptItemDraft[]
): {rowError?: string; receiptError?: string} => {
  const hasReceivedItem = items.some((item) => normalizeNumber(item.currentReceivedQty) > 0)
  if (!hasReceivedItem) {
    return {receiptError: 'Enter a received quantity for at least one item'}
  }

  for (const item of items) {
    const remaining = Math.max(Number(item.remainingQty), 0)
    const raw = normalizeNumber(item.currentReceivedQty)
    if (!Number.isFinite(raw) || !Number.isInteger(raw)) {
      return {
        rowError: `Received quantity must be a whole number for "${item.description}"`,
      }
    }
    if (raw < 0) {
      return {
        rowError: `Received quantity must be zero or greater for "${item.description}"`,
      }
    }
    if (raw > remaining) {
      return {
        rowError: `Received quantity (${raw}) exceeds remaining quantity (${remaining}) for "${item.description}"`,
      }
    }
  }

  return {}
}

export const buildGoodsReceiptPayload = (
  purchaseOrderId: number,
  receivedDate: string,
  receivedBy: string,
  vendorRating: number,
  generalNotes: string,
  items: ReceiptItemDraft[]
): CreateGoodsReceiptRequest => ({
  purchaseOrderId,
  receivedDate,
  receivedBy,
  vendorRating,
  generalNotes,
  lines: items
    .filter((item) => normalizeNumber(item.currentReceivedQty) > 0)
    .map((item) => ({
      requisitionLineId: item.requisitionLineId ?? undefined,
      inventoryItemId:
        typeof item.inventoryItemId === 'number' && !Number.isNaN(item.inventoryItemId)
          ? item.inventoryItemId
          : null,
      itemDescription: item.description,
      partNumber: item.partNumber?.trim() || undefined,
      expectedQty: item.expectedQty,
      receivedQty: Number(item.currentReceivedQty),
      condition: item.condition,
      notes: item.notes?.trim() ? item.notes.trim() : undefined,
    })),
})
